import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Mic, RotateCcw } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { CoachSessionDebrief, DebriefFieldName, DebriefUpdate } from '@/types/coach';

const FIELD_LABELS: Record<DebriefFieldName, string> = {
  toolsUsed: 'Eingesetzte Tools',
  mood: 'Stimmung in der Sitzung',
  keyInsights: 'Wichtige Erkenntnisse',
  improvements: 'Was könnte besser laufen',
  nextSteps: 'Nächste Schritte',
};

const FIELD_ORDER: DebriefFieldName[] = [
  'toolsUsed', 'mood', 'keyInsights', 'improvements', 'nextSteps',
];

const SHORT_THRESHOLD = 6;

interface Props {
  sessionId: string;
  debrief: CoachSessionDebrief;
  onChanged: (debrief: CoachSessionDebrief) => void;
  onResetRequested: () => void;
}

function humanList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} und ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} und ${items[items.length - 1]}`;
}

export function DebriefFields({ sessionId, debrief, onChanged, onResetRequested }: Props) {
  const [values, setValues] = useState<Record<DebriefFieldName, string>>({
    toolsUsed: debrief.toolsUsed,
    mood: debrief.mood,
    keyInsights: debrief.keyInsights,
    improvements: debrief.improvements,
    nextSteps: debrief.nextSteps,
  });
  const [saving, setSaving] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [refineDismissed, setRefineDismissed] = useState(false);
  const [activeRefineField, setActiveRefineField] = useState<DebriefFieldName | null>(null);

  useEffect(() => {
    setValues({
      toolsUsed: debrief.toolsUsed,
      mood: debrief.mood,
      keyInsights: debrief.keyInsights,
      improvements: debrief.improvements,
      nextSteps: debrief.nextSteps,
    });
  }, [debrief]);

  const dirty = useMemo(() => {
    return FIELD_ORDER.some((f) => values[f].trim() !== debrief[f].trim());
  }, [values, debrief]);

  const shortFields = useMemo(() => {
    return FIELD_ORDER.filter((f) => values[f].trim().length < SHORT_THRESHOLD);
  }, [values]);

  async function save() {
    setSaving(true);
    try {
      const data: DebriefUpdate = {
        toolsUsed: values.toolsUsed.trim(),
        mood: values.mood.trim(),
        keyInsights: values.keyInsights.trim(),
        improvements: values.improvements.trim(),
        nextSteps: values.nextSteps.trim(),
      };
      const updated = await api.coach.sessions.updateDebrief(sessionId, data);
      onChanged(updated);
      toast.success('Nachbereitung gespeichert.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    const ok = window.confirm('Aktuelle Nachbereitung verwerfen und neu starten?');
    if (!ok) return;
    onResetRequested();
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1.5">
          <RotateCcw size={14} /> Neu aufnehmen
        </Button>
      </div>

      {FIELD_ORDER.map((f) => (
        <FieldRow
          key={f}
          name={f}
          label={FIELD_LABELS[f]}
          value={values[f]}
          onChange={(v) => setValues((prev) => ({ ...prev, [f]: v }))}
          isRefiningHere={activeRefineField === f}
          onRefineDone={(updatedDebrief) => {
            onChanged(updatedDebrief);
            setActiveRefineField(null);
            setRefineDismissed(true);
          }}
          onRefineCancel={() => setActiveRefineField(null)}
          sessionId={sessionId}
        />
      ))}

      {!refineDismissed && shortFields.length > 0 && activeRefineField === null && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="text-amber-900">
            💬 Du hast {humanList(shortFields.map((f) => FIELD_LABELS[f]))} kaum erwähnt — magst du noch was sagen?
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {shortFields.map((f) => (
              <Button
                key={f}
                size="sm"
                variant="outline"
                onClick={() => setActiveRefineField(f)}
                className="gap-1.5"
              >
                <Mic size={12} /> {FIELD_LABELS[f]} nachschießen
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setRefineDismissed(true)}>
              Lassen wir's so
            </Button>
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowTranscript((s) => !s)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {showTranscript ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Roh-Transkript {showTranscript ? 'ausblenden' : 'anzeigen'}
        </button>
        {showTranscript && (
          <pre className="mt-2 rounded-md bg-muted/50 p-3 text-xs whitespace-pre-wrap font-mono">
            {debrief.rawTranscript || '(kein Transkript — manuell erfasst)'}
          </pre>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={!dirty || saving}>
          {saving ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}

interface FieldRowProps {
  name: DebriefFieldName;
  label: string;
  value: string;
  onChange: (v: string) => void;
  isRefiningHere: boolean;
  onRefineDone: (debrief: CoachSessionDebrief) => void;
  onRefineCancel: () => void;
  sessionId: string;
}

function FieldRow({ name, label, value, onChange, isRefiningHere, onRefineDone, onRefineCancel, sessionId }: FieldRowProps) {
  const recorder = useAudioRecorder();
  const [phase, setPhase] = useState<'idle' | 'recording' | 'sending'>('idle');

  useEffect(() => {
    if (!isRefiningHere && phase !== 'idle') {
      void recorder.stop();
      recorder.reset();
      setPhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefiningHere]);

  async function startRefine() {
    await recorder.start();
    setPhase('recording');
  }

  async function stopRefine() {
    setPhase('sending');
    const blob = await recorder.stop();
    if (!blob || blob.size < 1024) {
      toast.error('Aufnahme zu kurz.');
      setPhase('idle');
      onRefineCancel();
      return;
    }
    try {
      const { transcript } = await api.coach.sessions.transcribe(blob);
      if (!transcript.trim()) throw new Error('Whisper hat keinen Text erkannt.');
      const updated = await api.coach.sessions.refineDebrief(sessionId, name, transcript.trim());
      onRefineDone(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Nachschuss fehlgeschlagen.');
      setPhase('idle');
      onRefineCancel();
    }
  }

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium block">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-input bg-background p-3 text-sm min-h-[60px] resize-y"
        placeholder="—"
      />
      {isRefiningHere && (
        <div className="rounded-md border border-border bg-muted/30 p-3 mt-2 space-y-2">
          {phase === 'idle' && (
            <Button size="sm" onClick={startRefine} className="gap-1.5">
              <Mic size={12} /> Aufnahme starten
            </Button>
          )}
          {phase === 'recording' && (
            <div className="flex items-center justify-between">
              <span className="text-xs flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Aufnahme läuft…
              </span>
              <Button size="sm" onClick={stopRefine}>Stop</Button>
            </div>
          )}
          {phase === 'sending' && <span className="text-xs">Sende…</span>}
          {phase !== 'sending' && (
            <button
              type="button"
              onClick={onRefineCancel}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
